import { ChangeDetectorRef, Component } from '@angular/core';
import { ContactMessage } from './contact-message.model';
import { ContactMessageService } from './contact-message.service';

type ContactStatusFilter = 'all' | 'pending' | 'resolved';

@Component({
  selector: 'app-contact-message',
  standalone: false,
  templateUrl: './contact-message.component.html',
  styleUrls: ['./contact-message.component.css']
})
export class ContactMessageComponent {

  successMessage = '';
  errorMessage = '';

  messages: ContactMessage[] = [];
  filteredMessages: ContactMessage[] = [];

  currentPage = 1;
  pageSize = 10;
  searchText = '';
  statusFilter: ContactStatusFilter = 'all';

  isModalOpen = false;
  selectedMessage: ContactMessage | null = null;
  isLoadingDetails = false;
  resolvingMessageId: number | null = null;
  deletingMessageId: number | null = null;

  constructor(
    private contactMessageService: ContactMessageService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadMessages();
  }

  get totalCount(): number {
    return this.messages.length;
  }

  get pendingCount(): number {
    return this.messages.filter(message => !message.isSolved).length;
  }

  get resolvedCount(): number {
    return this.messages.filter(message => message.isSolved).length;
  }

  get todayCount(): number {
    const today = new Date();

    return this.messages.filter(message => {
      const createdAt = new Date(message.createdAt);
      return !Number.isNaN(createdAt.getTime())
        && createdAt.getFullYear() === today.getFullYear()
        && createdAt.getMonth() === today.getMonth()
        && createdAt.getDate() === today.getDate();
    }).length;
  }

  get pagedMessages(): ContactMessage[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredMessages.slice(start, start + this.pageSize);
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages() }, (_, index) => index + 1);
  }

  loadMessages(): void {
    this.contactMessageService.getAll().subscribe({
      next: (res: any) => {
        const apiData: any[] = res?.data || res || [];
        this.messages = apiData
          .map(item => this.mapMessage(item))
          .sort((a, b) => this.toTimestamp(b.createdAt) - this.toTimestamp(a.createdAt));
        this.applyFilters(false);
      },
      error: () => this.exceptionData('Failed to load contact messages.')
    });
  }

  applyFilters(resetPage = true): void {
    const keyword = this.searchText.trim().toLowerCase();

    if (resetPage) {
      this.currentPage = 1;
    }

    this.filteredMessages = this.messages.filter(message => {
      const matchesStatus =
        this.statusFilter === 'all'
        || (this.statusFilter === 'pending' && !message.isSolved)
        || (this.statusFilter === 'resolved' && message.isSolved);

      const status = message.isSolved ? 'resolved solved' : 'pending open';
      const matchesSearch = !keyword
        || `${message.fullName} ${message.email} ${message.subject} ${message.message} ${status} ${message.createdAt}`
          .toLowerCase()
          .includes(keyword);

      return matchesStatus && matchesSearch;
    });

    const maxPage = this.totalPages();
    if (this.currentPage > maxPage) {
      this.currentPage = maxPage;
    }
  }

  setStatusFilter(status: ContactStatusFilter): void {
    this.statusFilter = status;
    this.applyFilters();
  }

  resetFilters(): void {
    this.searchText = '';
    this.statusFilter = 'all';
    this.applyFilters();
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredMessages.length / this.pageSize));
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages()) {
      return;
    }

    this.currentPage = page;
  }

  viewData(id: number): void {
    this.isLoadingDetails = true;

    this.contactMessageService.getById(id).subscribe({
      next: (res: any) => {
        const data = res?.data || res;

        if (!data) {
          this.exceptionData('Contact message was not found.');
          this.isLoadingDetails = false;
          return;
        }

        this.selectedMessage = this.mapMessage(data);
        this.isModalOpen = true;
        this.isLoadingDetails = false;
      },
      error: () => {
        this.isLoadingDetails = false;
        this.exceptionData('Failed to load contact message.');
      }
    });
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.selectedMessage = null;
  }

  resolveMessage(message: ContactMessage): void {
    if (message.isSolved || this.resolvingMessageId !== null) {
      return;
    }

    if (!confirm('Mark this contact message as resolved?')) {
      return;
    }

    this.resolvingMessageId = message.contactMessageId;

    this.contactMessageService.markAsSolved(message.contactMessageId).subscribe({
      next: (res: any) => {
        const status = res?.status || 'success';
        const responseData = res?.data;

        if (status !== 'success' || responseData === false) {
          this.resolvingMessageId = null;
          this.exceptionData(res?.message || 'Failed to resolve contact message.');
          return;
        }

        this.resolvingMessageId = null;
        this.refreshData(
          status,
          res?.message || 'Contact message marked as resolved.'
        );
      },
      error: () => {
        this.resolvingMessageId = null;
        this.exceptionData('Failed to resolve contact message.');
      }
    });
  }

  deleteMessage(message: ContactMessage): void {
    if (this.deletingMessageId !== null) {
      return;
    }

    if (!confirm('Permanently delete this contact message?')) {
      return;
    }

    this.deletingMessageId = message.contactMessageId;

    this.contactMessageService.delete(message.contactMessageId).subscribe({
      next: (res: any) => {
        const status = res?.status || 'success';
        const responseData = res?.data;

        if (status !== 'success' || responseData === false) {
          this.deletingMessageId = null;
          this.exceptionData(res?.message || 'Failed to delete contact message.');
          return;
        }

        this.deletingMessageId = null;
        this.refreshData(status, res?.message || 'Contact message deleted.');
      },
      error: () => {
        this.deletingMessageId = null;
        this.exceptionData('Failed to delete contact message.');
      }
    });
  }

  clearMsg(): void {
    this.successMessage = '';
    this.errorMessage = '';
  }

  refreshData(status: string, message: string): void {
    if (status === 'success') {
      this.successMessage = message;
      this.closeModal();
      this.loadMessages();
    } else {
      this.errorMessage = message;
    }

    setTimeout(() => {
      this.clearMsg();
      this.cdr.detectChanges();
    }, 3000);
  }

  exceptionData(message: string): void {
    this.errorMessage = message;
    setTimeout(() => {
      this.clearMsg();
      this.cdr.detectChanges();
    }, 3000);
  }

  trackByMessageId(_: number, message: ContactMessage): number {
    return message.contactMessageId;
  }

  getReplyHref(message: ContactMessage): string {
    if (!message.email) {
      return '';
    }

    const subject = message.subject ? `Re: ${message.subject}` : 'Re: Contact enquiry';
    return `mailto:${message.email}?subject=${encodeURIComponent(subject)}`;
  }

  private mapMessage(item: any): ContactMessage {
    return {
      contactMessageId: item.contactMessageId ?? item.id ?? item.messageId ?? 0,
      fullName: item.fullName ?? item.name ?? item.senderName ?? '',
      email: item.email ?? item.emailAddress ?? '',
      subject: item.subject ?? item.title ?? '',
      message: item.message ?? item.yourMessage ?? item.content ?? item.description ?? '',
      isSolved: item.isSolved ?? item.solved ?? false,
      createdAt: item.createdAt ?? item.createdDate ?? item.createdOn ?? item.sentAt ?? '',
      solvedAt: item.solvedAt ?? item.isSolvedDate ?? item.solvedDate ?? null
    };
  }

  private toTimestamp(value: string | null | undefined): number {
    const timestamp = Date.parse(value || '');
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }
}
